import { Box, Button, Checkbox, Typography } from '@mui/material'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { queryClient } from '../../../../App'
import FormInput from '../../../../components/customElements/FormInput'
import CustomAccordion from '../../../../components/customElements/RequestDataAccordions/utils/CustomAccordion'
import RightDrawer from '../../../../components/customElements/RightDrawer/RightDrawer'
import FormField from '../../../../components/Form/FormField'
import { useGetReceiverModels } from '../../../../hooks/useGetReceiverModels'
import { isAdmin } from '../../../../services/AccountService'
import { notifySuccess } from '../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  isModalOpen,
  modalData,
} from '../../../../utils/controllers/ModalController'
import FormAutocomplete from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import { imageDuplicateErrorHandler } from '../../../SingleServiceRequest/helpers/rework-drawer.helpers'
import { useToReworkDrawer } from '../../../SingleServiceRequest/hooks/useToReworkDrawer'
import AddPhotosBlock from './DrawersCommonComponents/AddPhotosBlock'
import { validateNumberInput } from './DrawersCommonComponents/ServiceInformationAccordion'

const IntermediateDrawer = ({ serviceStore }) => {
  const intl = useIntl()
  const {
    currentServiceData,
    intermediateDrawerFormStore,
    solvewithoutvisitFunc,
    improvementRequiredFunc,
    serviceImpossibleFunc,
    markServiceOrderAsDuplicateFunc,
    uploadServicePhotoSettings,
    guaranteeRepairFunc,
    notGuaranteeRepairFunc,
    updateCurrentServiceData,
    updateOrders,
    handleReLoad,
  } = serviceStore

  const {
    watch,
    isLoading,
    disabled,
    control,
    impossibleReasons,
    showExplanation,
    onSubmit,
  } = useToReworkDrawer('to-impossible', currentServiceData?.id, true)
  const [wasReplaced, setWasReplaced] = useState(true) //была ли произведена замена приемника

  const { data: receiveresModels } = useGetReceiverModels()

  // определяю тип текущего дровера в зависимости от активного модального окна
  const currentDrawerType = modalData['intermediate-drawer']?.type || null

  // стейт для контроля отображаемых фотографий при загрузке их в поле для фотографий
  const [showImages, setShowImages] = useState([])

  // текущий заголовок аккордеона
  const getCurrentSummary = () => {
    switch (currentDrawerType) {
      case 'problem-solved-remotely':
        return 'Проблема решена дистанционно'
      case 'guarantee-repair':
        return 'Гарантийный ремонт'
      case 'not-guarantee-repair':
        return 'Негарантийный ремонт'
      case 'return-for-revision':
        return 'Вернуть на доработку'
      case 'service-is-not-possible':
        return 'Обслуживание не выполнено'
      case 'transfer-to-duplicate':
        return 'Перевод в дубликат'
      default:
        return null
    }
  }

  // текущий текст кнопки
  const getCurrentBtnText = () => {
    switch (currentDrawerType) {
      case 'problem-solved-remotely':
        return 'Завершить обслуживание'
      case 'guarantee-repair':
        return 'Подтвердить'
      case 'not-guarantee-repair':
        return 'Подтвердить'
      case 'return-for-revision':
        return 'Вернуть на доработку'
      case 'service-is-not-possible':
        return 'Обслуживание не выполнено'
      case 'transfer-to-duplicate':
        return 'Перевести в дубликат'
      default:
        return null
    }
  }

  // текущий функционал кнопки
  const getCurrentBtnAction = () => {
    switch (currentDrawerType) {
      case 'problem-solved-remotely':
        return (async () => {
          await solvewithoutvisitFunc()
          queryClient.invalidateQueries({ queryKey: ['service-table'] })
        })()
      case 'guarantee-repair':
        return guaranteeRepairFunc(receiveresModels, wasReplaced)
      case 'not-guarantee-repair':
        return notGuaranteeRepairFunc(receiveresModels, wasReplaced)
      case 'return-for-revision':
        return (async () => {
          await improvementRequiredFunc()
          queryClient.invalidateQueries({ queryKey: ['service-table'] })
          queryClient.invalidateQueries({
            queryKey: ['get-service-history'],
          })
        })()
      case 'service-is-not-possible':
        return (async () => {
          const res = await onSubmit(intermediateDrawerFormStore.data['files'])
          const imageError = imageDuplicateErrorHandler(res, true)

          if (!imageError) {
            handleReLoad()
            intermediateDrawerFormStore.clear()
            notifySuccess('Заявка обновлена')
            closeModalFn['intermediate-drawer']()
            closeModalFn['common-service-drawer']?.()
          }
        })()

      case 'transfer-to-duplicate':
        return (async () => {
          const callback = () => {
            queryClient.invalidateQueries({ queryKey: ['service-table'] })
            queryClient.invalidateQueries({
              queryKey: ['get-service-history'],
            })
          }
          await markServiceOrderAsDuplicateFunc({ top: false }, callback)
        })()
      default:
        return null
    }
  }

  // название поля для выбора настроек загрузки фотографий
  const getPhotoUploadSettingField = () => {
    switch (currentDrawerType) {
      case 'service-is-not-possible':
        return 'MaintenanceImpossible'
      case 'return-for-revision':
        return 'ImprovementRequired'
      default:
        return null
    }
  }

  // лейбл для текстового поля
  const getFormInputLabel = () => {
    switch (currentDrawerType) {
      case 'problem-solved-remotely':
        return intl.formatMessage({
          id: 'Основание для завершения обслуживания',
        })
      case 'guarantee-repair':
        return intl.formatMessage({ id: '№ заявки в сервис-центре' })
      case 'not-guarantee-repair':
        return intl.formatMessage({ id: '№ заявки в сервис-центре' })
      case 'return-for-revision':
        return intl.formatMessage({ id: 'Причина для передачи на доработку' })
      case 'service-is-not-possible':
        return intl.formatMessage({ id: 'Описание' })
      case 'transfer-to-duplicate':
        return intl.formatMessage({ id: 'Причина для перевода в дубликат' })
      default:
        return null
    }
  }

  // при открытии дровера устанавливаю orderId в формсторе
  useEffect(() => {
    if (isModalOpen['intermediate-drawer']) {
      intermediateDrawerFormStore.data.orderId = currentServiceData?.id
      !wasReplaced && setWasReplaced(true)
    }
  }, [currentServiceData?.id, isModalOpen['intermediate-drawer']])

  // обновление списка отображаемых фотографий. Срабатывает когда в сервисСторе очищаю поле файлов формсторы
  useEffect(() => {
    if (!intermediateDrawerFormStore.data['files']) {
      setShowImages([])
    }
  }, [intermediateDrawerFormStore.data['files']])

  // закрытие дровера
  const handleCloseDrawer = () => {
    closeModalFn['intermediate-drawer']()
    intermediateDrawerFormStore.clear()
    setShowImages([])
  }

  // настройка дизейбла кнопки в зависимости от полученных настроек загрузки фотографий с сервера
  let isDisabled
  const photosListLength = intermediateDrawerFormStore.data.files?.length || 0
  const settings =
    uploadServicePhotoSettings?.find(
      (item) => item.key === getPhotoUploadSettingField(),
    ) || {}
  if (settings) {
    const { maxLimitPerLoad, minLimitPerLoad, optional } = settings
    isDisabled = optional
      ? false
      : photosListLength < minLimitPerLoad || photosListLength > maxLimitPerLoad
  }

  const disableBtnByModel =
    (currentDrawerType === 'guarantee-repair' ||
      currentDrawerType === 'not-guarantee-repair') &&
    wasReplaced
      ? !intermediateDrawerFormStore.data.replacedReceiverModelId ||
        !intermediateDrawerFormStore.data.replacedReceiverSerialNumber
      : false

  const showFirstVComment =
    currentDrawerType === 'guarantee-repair' ||
    currentDrawerType === 'not-guarantee-repair'

  const showSecondVComment =
    !showFirstVComment && currentDrawerType !== 'service-is-not-possible'

  const disableV1 =
    !disableBtnByModel && !isDisabled && !intermediateDrawerFormStore.isValid
  const disabledMain =
    currentDrawerType === 'service-is-not-possible' ? disabled : disableV1
  return (
    <RightDrawer
      open={isModalOpen['intermediate-drawer']}
      onClose={handleCloseDrawer}
      titleComp={
        <Box className={'fx-col'}>
          <Typography variant={'subtitle1'} color={'common.white'}>
            <FormattedMessage
              id={`Заявка на обслуживание №${String(
                currentServiceData?.id,
              ).padStart(6, '0')}`}
            />
          </Typography>
        </Box>
      }
    >
      <>
        {!isAdmin() && (
          <CustomAccordion
            summary={getCurrentSummary()}
            defaultOpenState={true}
            preventHandleOpen={false}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginTop: '12px',
              }}
            >
              {getPhotoUploadSettingField() ? (
                <AddPhotosBlock
                  settingsField={getPhotoUploadSettingField()}
                  formStore={intermediateDrawerFormStore}
                  serviceStore={serviceStore}
                />
              ) : null}
              {showFirstVComment && (
                <>
                  <FormInput
                    api={intermediateDrawerFormStore}
                    type={'text'}
                    propName={'comment'}
                    fullWidth
                    label={getFormInputLabel()}
                    withoutErrorText={true}
                    validName={'external-tracking-number'}
                    error={!intermediateDrawerFormStore.data.comment.trim()}
                  />
                  {wasReplaced && (
                    <>
                      <FormInput
                        api={intermediateDrawerFormStore}
                        type={'select'}
                        propName={'replacedReceiverModelId'}
                        fullWidth
                        label={intl.formatMessage({
                          id: 'Модель нового оборудования',
                        })}
                        withoutErrorText={true}
                        error={
                          wasReplaced &&
                          !intermediateDrawerFormStore.data
                            .replacedReceiverModelId
                        }
                        items={receiveresModels.map(
                          (el) => `${el.manufacturerName} ${el.name}`, // Nova 01 || Nova 02
                        )}
                      />
                      <FormInput
                        validateFilterInputs={validateNumberInput}
                        api={intermediateDrawerFormStore}
                        type={'text'}
                        propName={'replacedReceiverSerialNumber'}
                        fullWidth
                        label={intl.formatMessage({
                          id: 'Cерийный номер нового оборудования',
                        })}
                        withoutErrorText={true}
                        inputProps={{
                          maxLength: 24,
                        }}
                        error={
                          wasReplaced &&
                          !intermediateDrawerFormStore.data
                            .replacedReceiverSerialNumber
                        }
                      />
                    </>
                  )}
                  <Box className={'fx-nowrap aln-center'}>
                    <Checkbox
                      checked={!wasReplaced}
                      onChange={() => setWasReplaced((prev) => !prev)}
                      sx={{ padding: 0, marginRight: '10px' }}
                    />
                    <Typography variant='body1'>
                      Замена не производилась
                    </Typography>
                  </Box>
                </>
              )}

              {showSecondVComment && (
                <FormInput
                  api={intermediateDrawerFormStore}
                  type={'text'}
                  propName={'comment'}
                  fullWidth
                  label={getFormInputLabel()}
                  withoutErrorText={true}
                  multiline
                  maxRows={4}
                  ignoreErrorColor={true}
                />
              )}

              {currentDrawerType === 'service-is-not-possible' && (
                <>
                  <FormAutocomplete
                    control={control}
                    fullWidth
                    name='impossibleReasonId'
                    options={
                      impossibleReasons?.map((reason) => reason.name) ?? []
                    }
                    label='Причина'
                    error={!watch('impossibleReasonId')}
                    withClearBtn
                    withTooltip
                  />
                  {showExplanation && (
                    <FormField
                      control={control}
                      name='impossibleReasonCustomDescription'
                      fullWidth
                      label='Описание'
                      multiline
                      rows={2}
                      error={!watch('impossibleReasonCustomDescription').trim()}
                    />
                  )}
                </>
              )}

              <Button
                fullWidth
                variant='contained'
                onClick={getCurrentBtnAction}
                disabled={disabledMain}
              >
                {getCurrentBtnText()}
              </Button>
            </Box>
          </CustomAccordion>
        )}
      </>
    </RightDrawer>
  )
}

export default observer(IntermediateDrawer)
